import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { NEVER, Observable, OperatorFunction, catchError, exhaustMap, of, pipe } from 'rxjs';

import { Logger } from '@mobi/rwc-utils-common-ng-jslib';

import { POST_LOGIN_URL } from '../authentication-config.provider';

const AUTH_ATTEMPT_CPT = `auth_attempt_cpt`;
const normalize = (url: URL) => (url.href.endsWith('/') ? url.href.slice(0, -1) : url.href);

@Injectable({ providedIn: 'root' })
export class CipAuthHelperService {
    #logger = inject(Logger).createCustomLogger('OAUTH');
    #oauthService = inject(OidcSecurityService);
    #window = inject(DOCUMENT).defaultView as Window;

    processAuthentication(configId: string): OperatorFunction<boolean, true | never> {
        return pipe(
            // exhaustMap because authorize will redirect the app, but not immediately, one must avoid a 2nd call to it.
            exhaustMap(isAuthenticated => (isAuthenticated ? of(true as const) : this.authorize(configId))),
            catchError(exception => {
                const attempt = +(this.#window.sessionStorage.getItem(AUTH_ATTEMPT_CPT) ?? '0') + 1;
                this.#window.sessionStorage.setItem(AUTH_ATTEMPT_CPT, `${attempt}`);
                this.#logger.error(`Error during authentication check, attempt to relogin ${attempt}/3`, { exception });
                if (attempt <= 3) {
                    this.#oauthService.authorize(configId);
                }
                throw exception;
            })
        );
    }

    authorize(configId: string): Observable<never> {
        this.savePostLoginUrl();
        this.#oauthService.authorize(configId);
        return NEVER;
    }

    savePostLoginUrl(url = new URL(this.#window.location.href)): void {
        const authFlowUrls = new Set(
            this.#oauthService
                .getConfigurations()
                .flatMap(config => [config.postLogoutRedirectUri, config.redirectUrl])
                .filter(Boolean)
        );
        // only save the url as post login url if not part of the auth flow.
        if (!authFlowUrls.has(normalize(url))) {
            const { pathname, search } = url;
            this.#window.sessionStorage.setItem(POST_LOGIN_URL, `${pathname}${search}`);
        }
    }
}
